import * as React from 'react';
import { useQuery } from 'convex/react';
import { Button, Loading, Progress } from '@fragments-sdk/ui';
import { api } from '@/convex';
import type { Id } from '@/convex';
import {
  deriveScanBannerState,
  type ScanBannerState,
} from './scan-banner-state';
import styles from './scan-progress-banner.module.scss';

interface ScanProgressBannerProps {
  projectId: Id<'projects'> | undefined;
  repoFullName?: string;
  state?: ScanBannerState;
  /**
   * Re-run handler. When provided and the current state is retryable
   * (a failed/stalled run), the banner renders a "Try again" action so the
   * user is never stranded on a dead scan without a way to restart it.
   */
  onRetry?: () => void;
}

/**
 * Reactive banner that surfaces the current state of the project's most
 * recent full scan run. Hidden when nothing interesting is happening,
 * loud when a scan is in progress, deferred, or failed. Backed entirely
 * by Convex reactivity — no client polling.
 */
export function ScanProgressBanner({
  projectId,
  repoFullName,
  state: providedState,
  onRetry,
}: ScanProgressBannerProps) {
  const queriedState = useScanBannerState({
    projectId: providedState ? undefined : projectId,
    repoFullName,
  });
  const state = providedState ?? queriedState;

  if (state.kind === 'hidden') return null;

  return <ScanBannerBody state={state} onRetry={onRetry} />;
}

export function useScanBannerState({
  projectId,
  repoFullName,
}: {
  projectId: Id<'projects'> | undefined;
  repoFullName?: string;
}) {
  const run = useQuery(
    api.scanRuns.getLatestForProject,
    projectId ? { projectId, repoFullName, profile: 'full' } : 'skip',
  );

  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (!projectId) return;
    // Tick once a second while a recent-success state could fade — cheap
    // enough that we don't bother gating on the state.
    const interval = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(interval);
  }, [projectId]);

  return React.useMemo(() => deriveScanBannerState({ run, now }), [run, now]);
}

function ScanBannerBody({
  state,
  onRetry,
}: {
  state: ScanBannerState;
  onRetry?: () => void;
}) {
  const variantClass = (() => {
    switch (state.kind) {
      case 'deferred':
        return styles.deferred;
      case 'failed':
        return styles.failed;
      case 'recent-success':
        return styles.success;
      default:
        return undefined;
    }
  })();

  const className = variantClass
    ? `${styles.banner} ${variantClass}`
    : styles.banner;

  return (
    <div className={className} role="status" aria-live="polite">
      {state.isLive ? (
        <Loading.Inline
          className={styles.loader}
          size="sm"
          label={state.title}
          aria-hidden
        />
      ) : null}
      <div className={styles.body}>
        <span className={styles.title}>{state.title}</span>
        {state.detail ? (
          <span className={styles.detail}>{state.detail}</span>
        ) : null}
        {state.evidenceDetail ? (
          <span className={styles.detail}>{state.evidenceDetail}</span>
        ) : null}
        {state.errorMessage ? (
          <span className={styles.errorMessage}>{state.errorMessage}</span>
        ) : null}
      </div>
      {state.kind === 'queued' || state.kind === 'running' ? (
        <Progress
          className={styles.progress}
          value={state.progressPercent ?? null}
          size="sm"
          aria-label={state.title}
        />
      ) : null}
      {state.retryable && onRetry ? (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className={styles.retry}
          onClick={onRetry}
        >
          Try again
        </Button>
      ) : null}
    </div>
  );
}
